import { Box, Flex, Grid, Text } from '@chakra-ui/react';
import { FieldLabel } from 'src/components/Field';
import {
  Button,
  FormFeedback,
  Input,
  Modal,
  TagInput
} from '@morressier/alembic';
import { Editor } from '@tinymce/tinymce-react';
import { useFormContext } from 'react-hook-form';
import { EmailContentForm } from '.';
import { getFileUploadArrayFFDConfigs } from '../../utils/formHelpers';
import { get } from 'lodash';
import MultipleFileUpload from '../File/MultipleFileUpload';
import TestEmailSent from '../../pages/Organization/Workspace/Settings/Emails/TestEmailSent';
import { useState } from 'react';
import PreviewEmail from './PreviewEmail';
import { normalizeRecipients } from '../../utils/normalizeEmailRecipients';
import { useMutation } from '@tanstack/react-query';
import { getEmailTemplatePreview } from '../../api/email-service';
import { IEmailContentType } from '@publishing-engine/domain/models/email-templates';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp } from '@fortawesome/pro-solid-svg-icons';

interface Props {
  organizationId: string;
  showTestEmail?: boolean;
}

const EditUserEmailTemplate = ({
  organizationId,
  showTestEmail = false
}: Props) => {
  const {
    control,
    setValue,
    register,
    watch,
    trigger,
    formState: { errors }
  } = useFormContext<EmailContentForm>();

  const emailContent = watch('emailContent');
  const [openPreviewEmail, setOpenPreviewEmail] = useState(false);
  const apiKeyTinymce = import.meta.env.VITE_TINY_MCE_CLIENT_KEY;
  const selfHostedTinymce = import.meta.env.VITE_SELF_HOSTED_TINY_MCE;

  const bodyError = get(errors, 'emailContent.body');
  const [previewTemplate, setPreviewTemplate] =
    useState<IEmailContentType | null>(null);

  const [showBcc, setShowBcc] = useState<boolean>(false);

  const previewMutation = useMutation({
    mutationFn: getEmailTemplatePreview,
    onSuccess: data => {
      setPreviewTemplate(data.renderedEmail);
      setOpenPreviewEmail(true);
    },
    onError: () => {
      throw new Error(`Something went wrong for preview`);
    }
  });

  const fetchPreview = async () => {
    const template = {
      ...emailContent,
      recipients: normalizeRecipients(emailContent.recipients)
    };

    await previewMutation.mutateAsync({ template, event: {}, test: true });
  };

  return (
    <Flex gap={4} flexDirection="column">
      <Flex
        borderRadius="small"
        backgroundColor="surfacePrimaryFill"
        padding={4}
        flexDirection="column"
        gap={6}
      >
        <Grid
          templateColumns="150px 1fr"
          rowGap={3}
          columnGap={2}
          alignItems="flex-start"
        >
          <FieldLabel label="To" isRequired subText={undefined} />
          <Flex flexDirection="column" width="100%">
            <TagInput
              name="emailContent.recipients.to"
              control={control}
              isInvalid={Boolean(errors?.emailContent?.recipients?.to)}
              disabled
              onBlur={() => trigger('emailContent.recipients.to')}
            />
            {errors.emailContent?.recipients?.to && (
              <FormFeedback variant="error">
                {errors.emailContent.recipients.to.message as string}
              </FormFeedback>
            )}
          </Flex>

          <FieldLabel label="Cc" isRequired={false} subText={undefined} />
          <Flex alignItems="center" width="100%">
            <Flex flex="1" flexDirection="column">
              <TagInput
                name="emailContent.recipients.cc"
                control={control}
                placeholder="Enter optional recipient email address(es)"
              />
              {errors.emailContent?.recipients?.cc && (
                <FormFeedback variant="error">
                  {errors.emailContent.recipients.cc.message as string}
                </FormFeedback>
              )}
            </Flex>
            <Button
              ml={2}
              variant="secondary"
              onClick={() => setShowBcc(!showBcc)}
              iconRight={
                showBcc ? (
                  <FontAwesomeIcon icon={faCaretUp} />
                ) : (
                  <FontAwesomeIcon icon={faCaretDown} />
                )
              }
            >
              Bcc
            </Button>
          </Flex>

          {showBcc && (
            <>
              <FieldLabel label="Bcc" isRequired={false} subText={undefined} />
              <Flex flexDirection="column" width="100%">
                <TagInput
                  name="emailContent.recipients.bcc"
                  control={control}
                  placeholder="Enter hidden recipient email address(es)"
                />
                {errors.emailContent?.recipients?.bcc && (
                  <FormFeedback variant="error">
                    {errors.emailContent.recipients.bcc.message as string}
                  </FormFeedback>
                )}
              </Flex>
            </>
          )}

          <FieldLabel label="Subject" isRequired subText={undefined} />
          <Flex flexDirection="column" width="100%">
            <Input
              {...register('emailContent.subject')}
              isInvalid={Boolean(errors.emailContent?.subject)}
              placeholder="Enter email subject"
            />
            {errors.emailContent?.subject && (
              <FormFeedback variant="error">
                {errors.emailContent.subject.message as string}
              </FormFeedback>
            )}
          </Flex>
        </Grid>

        <Flex flexDirection="column" gap={2}>
          <Flex justifyContent="space-between" alignItems="center">
            <FieldLabel label="Body Text" isRequired subText={undefined} />
            <Button
              variant="secondary"
              onClick={() => fetchPreview()}
              disabled={previewMutation.isPending}
              isLoading={previewMutation.isPending}
            >
              Preview
            </Button>
          </Flex>

          <Box width="100%">
            <Box
              className={bodyError ? 'editorErrorContainer' : ''}
              width="100%"
            >
              <Editor
                {...(apiKeyTinymce
                  ? { apiKey: apiKeyTinymce }
                  : { tinymceScriptSrc: selfHostedTinymce })}
                licenseKey={selfHostedTinymce ? 'gpl' : ''}
                value={watch('emailContent.body')}
                onEditorChange={(content: string) =>
                  setValue('emailContent.body', content, {
                    shouldValidate: true,
                    shouldDirty: true
                  })
                }
                init={{
                  menubar: false,
                  statusbar: false,
                  paste_as_text: true,
                  plugins: ['math', 'autoresize'],
                  toolbar:
                    'undo redo | formatselect | bold italic underline strikethrough | ' +
                    'alignleft aligncenter alignright alignjustify | ' +
                    'bullist numlist outdent indent | ' +
                    'link image table hr charmap | code preview | math',
                  min_height: 400,
                  entity_encoding: 'raw'
                }}
              />
            </Box>
            {bodyError && (
              <FormFeedback variant="error">
                {bodyError.message as string}
              </FormFeedback>
            )}
          </Box>
        </Flex>
      </Flex>
      <Flex flexDirection="column" gap={2}>
        <MultipleFileUpload
          organizationId={organizationId}
          config={getFileUploadArrayFFDConfigs('emailContent.attachments')}
        />
      </Flex>
      {showTestEmail && (
        <Flex flexDirection="column" gap={4} mt={4}>
          <TestEmailSent
            template={emailContent}
            organizationId={organizationId}
          />
        </Flex>
      )}
      {openPreviewEmail && (
        <Modal isOpen onClose={() => setOpenPreviewEmail(false)} width="720px">
          <Flex paddingX={4} paddingTop={4} paddingBottom={0}>
            <Text textStyle="headingGroup">Preview Email</Text>
          </Flex>
          <Box margin={4}>
            <PreviewEmail template={previewTemplate} />
          </Box>
        </Modal>
      )}
    </Flex>
  );
};

export default EditUserEmailTemplate;
